import { APP_SETTINGS_KEY, DEFAULT_SETTINGS } from '@/config/localSettings';
import {
  PAGE_403,
  PAGE_404,
  PAGE_500,
  PAGE_DEFAULT,
  PAGE_LOGIN,
} from '@/constants';
import type { ApiResponse } from '@/types/api.config';
import { getNavigate } from '@/utils/navigation';
import type {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import axios from 'axios';

// Create axios instance factory function
export const createAxiosInstance = (
  baseURL: string,
  timeout: number = 30000
): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });

  // Request interceptor for axios instance
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      try {
        const raw = localStorage.getItem(APP_SETTINGS_KEY);
        const settings = raw
          ? { ...DEFAULT_SETTINGS, ...JSON.parse(raw) }
          : DEFAULT_SETTINGS;
        if (settings.language) {
          config.headers['X-Lang'] = settings.language;
        }
      } catch {
        config.headers['X-Lang'] = DEFAULT_SETTINGS.language;
      }

      if (config.data instanceof FormData) {
        delete config.headers['Content-Type'];
      }

      if (config.method === 'get') {
        config.params = {
          ...config.params,
          _t: Date.now(),
        };
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for axios instance
  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse<unknown>>) => {
      const navigate = getNavigate();
      const { data } = response;
      const header = data?.header;
      // Handle business errors
      if (!header?.isSuccessful || header?.resultCode !== 200) {
        // Handle different error codes based on the response data
        switch (header?.resultCode) {
          case 401:
            // Unauthorized, redirect to login page
            localStorage.removeItem('token');
            window.location.href = PAGE_LOGIN;
            break;
          case 403:
            // No permission
            console.error(header?.resultMessage || 'No permission');
            // window.location.href = PAGE_403;
            navigate(PAGE_DEFAULT);
            break;
          default:
            console.error(header?.resultMessage || 'Request failed');
            // message.error(header?.resultMessage || 'Request failed');
            break;
        }
      }

      return response;
    },
    (error) => {
      // Handle HTTP errors
      if (error.response) {
        const { status } = error.response;
        switch (status) {
          case 401:
            sessionStorage.removeItem('token');
            window.location.href = PAGE_LOGIN;
            break;
          case 403:
            console.error('No permission');
            window.location.href = PAGE_403;
            break;
          case 404:
            console.error('Resource not found');
            window.location.href = PAGE_404;
            break;
          case 500:
            console.error('Server internal error');
            window.location.href = PAGE_500;
            break;
          default:
            console.error(`HTTP error: ${status}`);
        }
      } else if (error.request) {
        console.error(
          'Network request failed, please check network connection'
        );
      } else {
        console.error('Request configuration error:', error.message);
      }

      return Promise.reject(error);
    }
  );

  return instance;
};
